import { Component, Host, h, Event, EventEmitter, Method, State, Prop, Watch } from '@stencil/core';
import { E_SEW_SEARCH_FIELD_SIZE } from '../../forms/sew-search-field/sew-search-field.types';
import { LabelParser } from '../sew-ws-header/helpers/labels.helpers';
import { DirectLinks } from '../sew-ws-header/types/direct-links.types';
import { Labels } from '../sew-ws-header/types/labels.types';
import { getSearchUrl } from './sew-ws-search.helper';

@Component({
    tag: 'sew-ws-search',
    styleUrl: 'sew-ws-search.scss',
    shadow: true,
})
export class SewWsSearch {
    @Event() fetchRecommendation: EventEmitter<string>;
    @Event() storeSearchHistory: EventEmitter<string>;

    /**
     * Base URL für the recommendation links
     * If not set, the link will be relative.
     *
     * @example https://www.sew-d.de
     */
    @Prop({ attribute: 'baseUrl' }) baseUrl = '';

    /**
     * language for the result of the recommendation links.
     * Must be set. Backup is english
     *
     * @exampel for german: de_de
     * */
    @Prop() language = 'en_us';
    @Prop() country = 'DE';

    @Prop() labels: Labels;
    @Prop({ attribute: 'directLinks' }) directLinks: DirectLinks | null = null;
    @Prop({ attribute: 'latestSearchResults' }) latestSearchResults: string[] | undefined;
    @Prop() open = false;

    @State() private recommendations: string[] = [];
    @State() private labelParser: LabelParser;
    @State() private noFilter = false;

    @Watch('language')
    watchLanguageChanges(): void {
        this.labelParser && this.generateDownloadTypes();
    }

    @Watch('country')
    watchCountryChanges(): void {
        this.labelParser && this.generateDownloadTypes();
    }

    @Watch('labels')
    public watchLabels(): void {
        this.labelParser = new LabelParser(this.labels);
        this.labelParser && this.generateDownloadTypes();
    }

    @Watch('open')
    setInputFocus(): void {
        if (this.open) {
            this.inputElement.focus();
        }
    }

    private generateDownloadTypes = (): void => {
        const country = this.country.toUpperCase();
        this.downloadTypesAsJson = [
            {
                name: this.labelParser.getLabel('CMS_SEARCH_TO_DSC'),
                value: `/os/dsc/?country=${country}&language=${this.language}`,
            },
        ];
    };

    @Method()
    async setRecommendations(recommendations: string[]): Promise<void> {
        this.recommendations = recommendations;
    }

    private downloadTypesAsJson = [];
    private searchTerm: string | null = null;
    private recommendationsTimeout: NodeJS.Timeout;
    private recommendationsDelay = 500;
    private inputElement: HTMLInputElement;

    componentWillLoad(): void {
        this.labelParser = new LabelParser(this.labels);
        this.generateDownloadTypes();
    }

    private handleSearchChange = (event: InputEvent) => {
        this.searchTerm = (event.target as HTMLInputElement).value;

        if (this.searchTerm.match(/\d{2}\.\d{10}/)) {
            this.noFilter = true;
            let downloadRecommendations = [];

            downloadRecommendations = this.downloadTypesAsJson.map(downloadType => downloadType.name);
            this.setRecommendations(downloadRecommendations);

            return;
        }

        this.noFilter = false;

        if (this.recommendationsTimeout) {
            clearTimeout(this.recommendationsTimeout);
        }
        this.recommendationsTimeout = setTimeout(() => {
            !this.noFilter && this.fetchRecommendation.emit(this.searchTerm);
        }, this.recommendationsDelay);
    };

    private handleKeyPress = (event: KeyboardEvent) => {
        if (event.key === 'Enter' && this.searchTerm?.trim().length > 0) {
            if (this.noFilter && this.downloadTypesAsJson.length === 1) {
                location.href = `${this.downloadTypesAsJson[0].value}&search=${this.searchTerm}`;
            } else {
                this.navigateToSearchResults(this.searchTerm.trim());
            }
        }
    };

    private recommendationClicked = (ev: CustomEvent<string>) => {
        if (this.noFilter) {
            const downloadType = this.downloadTypesAsJson.find(
                downloadType => downloadType.name === ev.detail
            );

            location.href = `${downloadType.value}&search=${this.searchTerm}`;
        } else if (ev.detail) {
            this.navigateToSearchResults(ev.detail);
        }
    };

    private navigateToSearchResults(searchTerm: string) {
        this.storeSearchHistory.emit(searchTerm);
    }

    render() {
        return (
            <Host>
                <sew-search-field
                    ws
                    size={E_SEW_SEARCH_FIELD_SIZE.L}
                    onItemClicked={this.recommendationClicked}
                    items={this.recommendations.join(',')}
                    noFilter={this.noFilter}
                >
                    <input
                        id="search"
                        type="text"
                        placeholder={this.labelParser.getLabel('CMS_HEADER_SEARCH_PLACEHOLDER')}
                        autocomplete="off"
                        onInput={this.handleSearchChange}
                        onKeyPress={this.handleKeyPress}
                        ref={el => (this.inputElement = el)}
                    />
                </sew-search-field>
                <div class="links-wrapper">
                    <div class="linklist">
                        {this.directLinks && (
                            <div>
                                <div class="links-headline">{this.directLinks.headline}</div>
                                {this.directLinks.links.map(item => (
                                    <sew-ws-search-link>
                                        <a href={item.href}>{item.text}</a>
                                    </sew-ws-search-link>
                                ))}
                            </div>
                        )}
                        {this.latestSearchResults && this.latestSearchResults.length > 0 && (
                            <div>
                                <div class="links-headline">
                                    {this.labelParser.getLabel('CMS_HEADER_SEARCH_LATEST_HEADLINE')}
                                </div>
                                {this.latestSearchResults.map(item => (
                                    <sew-ws-search-link>
                                        <a href={getSearchUrl(this.baseUrl, this.language, item)}>{item}</a>
                                    </sew-ws-search-link>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </Host>
        );
    }
}
